import { Box, Button, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useCallback, useContext, useMemo } from 'react';
import FileCopyIcon from '@material-ui/icons/FileCopy';

import { ImageViewerData } from 'components/image-viewer/types';
import { LabeledField } from 'components/labeled-field/labeled-field';
import { ArchivalAidMetadata, ArchivalUnitCopy } from 'types';
import { getFileFormat } from 'utils/get-file-format';
import { getArchivalUnitQuote } from 'modules/archival-unit/utils/get-archival-unit-quote';
import useFetch from 'utils/use-fetch';
import { ApiRoute } from 'enums';
import { copyToClipboard } from 'utils/copy-to-clipboard';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';

import { StructMapData } from './struct-map-types';
import { prettyPrintFileSize } from './utils';
import { findValidDescribingAidId } from 'components/grid/v2/context/hierarchy-context';

interface Props {
  data: ImageViewerData<StructMapData>;
  customProps?: {
    parentEntity?: ArchivalUnitCopy;
  };
}

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  copyButton: {
    marginTop: 'auto',
  },
}));

export const StructMapImageInfo = ({ data, customProps }: Props) => {
  const file = data.metadata.fileRef;
  const { parentEntity } = customProps ?? {};
  const { showNotification } = useContext(NotificationCtx);
  const classes = useStyles();

  const size = `${prettyPrintFileSize(file?.size)} MB`;

  const fileFormat = file?.contentType ? getFileFormat(file?.contentType) : '-';

  const aId = findValidDescribingAidId(
    parentEntity?.archivalUnitReference?.describingArchivalAids
  );

  const { data: archivalAid } = useFetch<ArchivalAidMetadata>(
    aId ? `/api/${ApiRoute.ARCHIVAL_AID}/${aId}` : ''
  );

  const quote = useMemo(
    () =>
      parentEntity
        ? getArchivalUnitQuote(
            parentEntity?.archivalUnitReference,
            archivalAid,
            parentEntity.summary,
            data.label
          )
        : undefined,
    [archivalAid, data.label, parentEntity]
  );

  const copyQuote = useCallback(async () => {
    if (!quote) {
      return;
    }

    await copyToClipboard(quote);
    showNotification('Citace byla zkopírována', NotificationVariant.SUCCESS);
  }, [quote, showNotification]);

  return (
    <Box className={classes.container}>
      <LabeledField
        label="Název"
        content={data.metadata.label ?? data.label ?? '-'}
      />
      <LabeledField label="Název souboru" content={file?.name ?? '-'} />
      <LabeledField label="Velikost" content={size} />
      <LabeledField label="Formát" content={fileFormat} />
      {quote && (
        <Tooltip title={quote}>
          <Button
            variant="outlined"
            onClick={copyQuote}
            className={classes.copyButton}
            startIcon={<FileCopyIcon />}
          >
            Zkopírovat citaci
          </Button>
        </Tooltip>
      )}
    </Box>
  );
};
